import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  I18nMessagesFilterParams,
  I18nMessageType,
  I18nMessagesResponse,
  CreateI18nMessageParams,
  UpdateI18nMessageParams
} from '@/types/i18n-message'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import {
  fetchI18nMessages,
  fetchI18nMessage,
  updateI18nMessage,
  deleteI18nMessage,
  createI18nMessage,
  bulkDeleteI18nMessages
} from '@/lib/api'
import { SortEnum } from '@/enums'

// Single property state type
type MessageStateType = {
  isLoading: boolean
  response: I18nMessageType | null
  error: {
    status?: number
    message: string
  } | null
}

// Messages list state type
type MessagesListStateType = {
  isLoading: boolean
  response: I18nMessagesResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type I18nMessagesStoreState = {
  // Messages list filters state
  filters: I18nMessagesFilterParams
  setFilters: (filters: Partial<I18nMessagesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Messages list state
  messagesListState: MessagesListStateType
  fetchI18nMessages: (params?: I18nMessagesFilterParams) => Promise<I18nMessagesResponse>
  resetMessagesList: () => void

  // Single property state
  messageState: MessageStateType
  getI18nMessage: (messageId: string) => Promise<I18nMessageType>
  createI18nMessage: (params: CreateI18nMessageParams) => Promise<I18nMessageType>
  updateI18nMessage: (messageId: string, params: UpdateI18nMessageParams) => Promise<I18nMessageType>
  deleteI18nMessage: (messageId: string) => Promise<void>
  resetMessage: () => void

  // Selection operations
  selectedMessageIds: string[]
  selectMessage: (id: string) => void
  deselectMessage: (id: string) => void
  toggleMessageSelection: (id: string) => void
  selectAllMessages: (messages: I18nMessageType[]) => void
  deselectAllMessages: () => void
  isMessageSelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteI18nMessages: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: I18nMessagesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt'
}

const initialMessageState: MessageStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialMessagesListState: MessagesListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useI18nMessagesStore = create<I18nMessagesStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    messageState: initialMessageState,
    messagesListState: initialMessagesListState,
    selectedMessageIds: [],

    setFilters: newFilters =>
      set(state => ({
        filters: { ...state.filters, ...newFilters }
      })),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: I18nMessagesFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value as I18nMessagesFilterParams['sortBy']
            break
          case 'q':
          case 'createdAtStart':
          case 'createdAtEnd':
          case 'updatedAtStart':
          case 'updatedAtEnd':
            filters[key] = value
            break
          case 'createdUsers':
          case 'updatedUsers':
            filters[key] = value.split(',').filter(Boolean)
            break
        }
      })

      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.append(key, value.join(','))
            }
          } else {
            params.append(key, value.toString())
          }
        }
      })

      return params
    },

    fetchI18nMessages: async (params = {}) => {
      set(() => ({
        messagesListState: {
          ...initialMessagesListState,
          isLoading: true
        }
      }))

      try {
        const finalParams = { ...get().filters, ...params }
        const data = await fetchI18nMessages(finalParams)

        set(() => ({
          messagesListState: {
            ...initialMessagesListState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          messagesListState: {
            ...initialMessagesListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetMessagesList: () => {
      set(() => ({
        messagesListState: initialMessagesListState
      }))
    },

    getI18nMessage: async (messageId: string) => {
      set(() => ({
        messageState: {
          ...initialMessageState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchI18nMessage(messageId)

        set(() => ({
          messageState: {
            ...initialMessageState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          messageState: {
            ...initialMessageState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createI18nMessage: async (params: CreateI18nMessageParams) => {
      set(() => ({
        messageState: {
          ...initialMessageState,
          isLoading: true
        }
      }))

      try {
        const data = await createI18nMessage(params)

        set(() => ({
          messageState: {
            ...initialMessageState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          messageState: {
            ...initialMessageState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        // 422 validation hatalarını sessizce handle et
        if (error.response?.status === 422) {
          // Validation hatalarını error olarak işaretle ama throw etme
          const validationError = new Error('Validation failed')
          ;(validationError as any).response = error.response
          ;(validationError as any).isValidationError = true
          throw validationError
        }

        throw error
      }
    },

    updateI18nMessage: async (messageId: string, params: UpdateI18nMessageParams) => {
      set(() => ({
        messageState: {
          ...get().messageState,
          isLoading: true
        }
      }))

      try {
        const data = await updateI18nMessage(messageId, params)

        set(() => ({
          messageState: {
            ...get().messageState,
            isLoading: false,
            response: data,
            error: null
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          messageState: {
            ...get().messageState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        // 422 validation hatalarını sessizce handle et
        if (error.response?.status === 422) {
          // Validation hatalarını error olarak işaretle ama throw etme
          const validationError = new Error('Validation failed')
          ;(validationError as any).response = error.response
          ;(validationError as any).isValidationError = true
          throw validationError
        }

        throw error
      }
    },

    deleteI18nMessage: async (messageId: string) => {
      set(() => ({
        messageState: {
          ...get().messageState,
          isLoading: true
        }
      }))

      try {
        await deleteI18nMessage(messageId)

        set(() => ({
          messageState: {
            ...get().messageState,
            isLoading: false,
            error: null
          }
        }))
      } catch (error: any) {
        set(() => ({
          messageState: {
            ...get().messageState,
            isLoading: false,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetMessage: () => {
      set(() => ({
        messageState: initialMessageState
      }))
    },

    // Selection operations
    selectMessage: id => {
      set(state => {
        if (state.selectedMessageIds.includes(id)) {
          return state
        }
        return { selectedMessageIds: [...state.selectedMessageIds, id] }
      })
    },

    deselectMessage: id => {
      set(state => ({
        selectedMessageIds: state.selectedMessageIds.filter(messageId => messageId !== id)
      }))
    },

    toggleMessageSelection: id => {
      set(state => {
        if (state.selectedMessageIds.includes(id)) {
          return { selectedMessageIds: state.selectedMessageIds.filter(messageId => messageId !== id) }
        }
        return { selectedMessageIds: [...state.selectedMessageIds, id] }
      })
    },

    selectAllMessages: messages => {
      set(() => ({
        selectedMessageIds: messages.map(message => message.id)
      }))
    },

    deselectAllMessages: () => {
      set(() => ({ selectedMessageIds: [] }))
    },

    isMessageSelected: id => {
      return get().selectedMessageIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteI18nMessages: async ids => {
      set(state => ({
        messageState: {
          ...state.messageState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteI18nMessages({ ids })

        set(state => ({
          messageState: {
            ...state.messageState,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedMessageIds: state.selectedMessageIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          messageState: {
            ...state.messageState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default useI18nMessagesStore
